import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, Star, Gift, Trophy, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyBanner, setVerifyBanner] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verify_sent') === '1') {
      const e = params.get('email') || '';
      setEmail(e);
      setVerifyBanner(`We've sent a verification link to ${e || 'your email'}. Click the link, then come back here to sign in.`);
    }
  }, [location.search]);

  const handleResend = async () => {
    const target = unverifiedEmail || email;
    if (!target) return;
    setResendLoading(true);
    setResendStatus('');
    const r = await resendVerification(target);
    setResendLoading(false);
    setResendStatus(r.success ? (r.message || 'Verification email sent.') : r.error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverifiedEmail('');
    setResendStatus('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      if (result.emailNotVerified) {
        const targetEmail = result.email || email;
        await resendVerification(targetEmail);
        navigate(`/verify-otp?email=${encodeURIComponent(targetEmail)}`);
        return;
      }
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="login-page">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none opacity-70"
        style={{ background: 'radial-gradient(circle at 20% 20%, rgba(239,68,68,0.18), transparent 45%), radial-gradient(circle at 90% 80%, rgba(239,68,68,0.10), transparent 45%)' }} />

      <div className="relative min-h-screen grid grid-cols-1 lg:grid-cols-2">

        {/* Left: brand + rewards pitch */}
        <div className="hidden lg:flex flex-col justify-between p-10 xl:p-14 border-r" style={{ borderColor: 'rgb(var(--border))' }}>
          <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="login-logo">
            <img src="/aptivon-logo-v2.png" alt="Aptivon" className="h-11 w-auto" />
            <span className="text-fg-faint text-[10px] uppercase tracking-widest">JEE · NEET OS</span>
          </Link>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 mb-4 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Star className="w-3 h-3 fill-current" style={{ color: 'rgb(var(--accent))' }} />
              <span>The Reward Wallet</span>
            </div>
            <h2 className="text-3xl xl:text-4xl font-bold text-fg tracking-tight leading-tight mb-4">
              Rank up. <span style={{ color: 'rgb(var(--accent))' }}>Cash out.</span>
            </h2>
            <p className="text-sm text-fg-muted mb-8 leading-relaxed">
              Every mock you crack earns points. Every rank you climb earns Super Points. Top 3% of Pro
              performers convert them into real Amazon &amp; Flipkart gift cards each month.
            </p>

            {/* Mini wallet showcase */}
            <div
              className="relative overflow-hidden rounded-xl border p-5 mb-6"
              style={{
                background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.12) 100%)',
                borderColor: 'rgba(239,68,68,0.35)',
              }}
              data-testid="login-wallet-preview"
            >
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl pointer-events-none"
                style={{ background: 'rgba(239,68,68,0.20)' }} />
              <div className="relative">
                <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2">Last month · Rank #37 payout</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <Star className="w-8 h-8 fill-current" style={{ color: 'rgb(var(--accent))' }} />
                  <span className="text-5xl font-bold text-fg tracking-tight">4,800</span>
                  <span className="text-xs text-fg-muted ml-1">Super Points</span>
                </div>
                <p className="text-sm text-fg-muted">
                  = <span className="text-fg font-semibold">₹2,400</span> in gift cards, delivered by email.
                </p>
              </div>
            </div>

            <ul className="space-y-3">
              {[
                { Icon: Trophy, text: 'Daily AI mocks + 1v1 battles + chapter mastery quizzes' },
                { Icon: Gift,   text: 'Redeem Super Points on Amazon, Flipkart, Unacademy, Aakash, Motion' },
                { Icon: CheckCircle2, text: '100% skill-based. Not gambling / RMG. Bots never earn payouts.' },
              ].map(({ Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-fg-secondary">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(239,68,68,0.12)' }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: 'rgb(var(--accent))' }} />
                  </div>
                  <span className="pt-1">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-6 text-[11px] text-fg-faint">
            <Link to="/terms" className="hover:text-fg transition">Terms</Link>
            <Link to="/privacy" className="hover:text-fg transition">Privacy</Link>
            <span className="font-mono ml-auto">v1.0 · Aptivon OS</span>
          </div>
        </div>

        {/* Right: form */}
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">

            {/* Mobile-only logo */}
            <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-8" data-testid="login-logo-mobile">
              <img src="/aptivon-logo-v2.png" alt="Aptivon" className="h-11 w-auto" />
              <span className="text-fg-faint text-[10px] uppercase tracking-widest">JEE · NEET OS</span>
            </Link>

            <div className="mb-8">
              <div className="inline-flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
                <ArrowRight className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
                <span>Sign in</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-fg tracking-tight mb-1.5">
                Welcome back.
              </h1>
              <p className="text-sm text-fg-muted">
                Continue climbing the leaderboard. Your Super Points are waiting.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              {verifyBanner && (
                <div
                  className="p-3 rounded-md text-xs border"
                  style={{ background: 'rgba(245,158,11,0.10)', borderColor: 'rgba(245,158,11,0.35)', color: 'rgb(var(--warning))' }}
                  data-testid="verify-banner"
                >
                  {verifyBanner}
                </div>
              )}
              {error && (
                <div
                  className="p-3 rounded-md text-xs border"
                  style={{ background: 'rgba(239,68,68,0.10)', borderColor: 'rgba(239,68,68,0.35)', color: 'rgb(var(--accent))' }}
                  data-testid="login-error"
                >
                  {error}
                  {unverifiedEmail && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendLoading}
                        className="font-semibold underline disabled:opacity-60"
                        data-testid="resend-verification-button"
                      >
                        {resendLoading ? 'Sending…' : 'Resend verification email'}
                      </button>
                      {resendStatus && (
                        <p className="mt-1 text-[11px] text-fg-secondary" data-testid="resend-status">{resendStatus}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-[11px] font-medium uppercase tracking-widest text-fg-faint mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-md text-sm outline-none transition"
                    style={{ background: 'rgb(var(--card))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--fg))' }}
                    placeholder="you@example.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[11px] font-medium uppercase tracking-widest text-fg-faint">Password</label>
                  <Link to="/forgot-password" className="text-[11px] font-medium text-fg-muted hover:text-fg transition" data-testid="forgot-password-link">
                    Forgot?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-11 py-3 rounded-md text-sm outline-none transition"
                    style={{ background: 'rgb(var(--card))', border: '1px solid rgb(var(--border))', color: 'rgb(var(--fg))' }}
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-faint hover:text-fg transition"
                    data-testid="toggle-password-visibility"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
                style={{ background: 'rgb(var(--accent))' }}
                data-testid="login-submit-button"
              >
                {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>

            {/* Divider */}
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: 'rgb(var(--border))' }} />
              <span className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">or</span>
              <div className="flex-1 h-px" style={{ background: 'rgb(var(--border))' }} />
            </div>

            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL — this is Emergent-managed Google Auth
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="mt-4 w-full inline-flex items-center justify-center gap-3 px-6 py-3 rounded-md text-sm font-semibold text-fg transition hover:bg-card-hover"
              style={{ background: 'rgb(var(--card))', border: '1px solid rgb(var(--border))' }}
              data-testid="google-login-button"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <p className="mt-6 text-sm text-fg-muted text-center">
              New to Aptivon?{' '}
              <Link to="/register" className="font-semibold hover:underline" style={{ color: 'rgb(var(--accent))' }} data-testid="register-link">
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
